import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = loading, false = logged out, object = user
  const [error, setError] = useState("");

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      // 401 on cold-load is expected before login; other transient errors
      // clear the session and let the user retry.
      setUser(false);
    }
  }, []);

  useEffect(() => {
    // If we're returning from the Emergent OAuth callback, skip the /auth/me
    // pre-check — AuthCallback will exchange the session_id and set our cookie
    // first, then explicitly call refresh() to re-hydrate `user`. Otherwise
    // this effect races the callback and sends the user to /login by mistake.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      return;
    }
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      // Auth token is set as httpOnly cookie by the backend — do NOT store in localStorage.
      setUser(data.user);
      return true;
    } catch (e) {
      setError(formatApiError(e));
      return false;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Best-effort: even if the logout API fails we still clear local state so
      // the user is signed out client-side.
    }
    setUser(false);
  }, []);

  const value = useMemo(
    () => ({ user, login, logout, error, refresh: checkSession }),
    [user, login, logout, error, checkSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
